import { useState, useEffect } from "react";
import { BRAND } from "../constants/brand";

/* ─── Data ──────────────────────────────────────────────────────────────── */

const TOOLS = [
  {
    id: "aiden",
    eyebrow: "For Patients",
    badge: "AIden",
    headline: "Your Lab Results,\nFinally Explained",
    tagline: "The 'AI' in AIden is no coincidence.",
    desc: "Lab results are full of numbers, clinical ranges, and medical shorthand that can leave anyone feeling anxious and confused. AIden — Labarium Diagnostic Systems' patient-facing AI assistant — translates your results into plain language you can actually use. Understand what each value means, what you can do in your daily life to improve your health and wellness, and what questions to ask your clinicians.",
    features: [
      { icon: "🔬", label: "AI-powered lab report analysis", detail: "Your results explained in plain language" },
      { icon: "📈", label: "Longitudinal trend memory", detail: "Watch your health progress across visits" },
      { icon: "🌐", label: "16-language support", detail: "Your results, in the language you think in" },
      { icon: "🔒", label: "HIPAA-compliant & private", detail: "Zero PHI ever sent to external AI models" },
    ],
    accentFrom: "#3b82f6",
    accentTo: "#6366f1",
    chatUrl: "https://portal.labarium.com",
    avatar: "/aiden-avatar.png",
    sampleFile: "/sample-aiden-analysis.pdf",
    sampleLabel: "Sample AIden Analysis",
  },
  {
    id: "claire",
    eyebrow: "For Clinicians",
    badge: "ClAIre",
    headline: "Clinical Decision Support\nThat Thinks With You",
    tagline: "The 'AI' in ClAIre is the engine behind every clear answer.",
    desc: "Evidence-based decision support designed for licensed healthcare providers. ClAIre turns a single data point into a complete contextual picture — risk stratification, clinical protocols, recommendations to consider, and peer-reviewed citations. The result: faster, more confident decisions at the point of care.",
    features: [
      { icon: "⚕️", label: "Risk stratification", detail: "Structured triage support for abnormal values" },
      { icon: "📚", label: "Peer-reviewed citations", detail: "Evidence-based recommendations, not guesswork" },
      { icon: "📊", label: "Trend analysis", detail: "Longitudinal context across patient visits" },
      { icon: "🔒", label: "HIPAA-compliant & private", detail: "Built for clinical environments" },
    ],
    accentFrom: "#10b981",
    accentTo: "#0891b2",
    chatUrl: "https://portal.labarium.com",
    avatar: "/claire-avatar.png",
    sampleFile: "/sample-claire-analysis.pdf",
    sampleLabel: "Sample ClAIre Analysis",
  },
];

const AI_REPORTS = [
  {
    badge: "PATIENT REPORT",
    accentFrom: "#3b82f6",
    accentTo: "#6366f1",
    headline: "The Labarium AI Patient Report",
    desc: "As a Labarium patient, you receive a formatted PDF report alongside your lab results. Instead of raw numbers and clinical shorthand, you get a report you can actually read — with every result explained in plain language, abnormal values clearly flagged, and personalized lifestyle and diet recommendations written specifically to your results.",
    features: [
      "Your demographics and test date",
      "All your test results with normal / abnormal status clearly marked",
      "AI-written plain-language summary of what your results mean",
      "Personalized lifestyle and diet recommendations based on your results",
    ],
    sampleFile: "/sample-patient-report.pdf",
    sampleLabel: "Sample Patient Report",
  },
  {
    badge: "CLINICIAN REPORT",
    accentFrom: "#10b981",
    accentTo: "#0891b2",
    headline: "The Labarium AI Clinician Report",
    desc: "As a Labarium clinician, you receive a structured clinical PDF alongside your patient's lab results. The report surfaces what matters most at the point of care — highlighted abnormal results, an AI-generated clinical summary, and the full test panel in a clean, organized format. Covers blood panels, drug screens, toxicology, and PCR pathogen panels.",
    features: [
      "Patient demographics and panel information",
      "Highlighted abnormal results with reference ranges",
      "AI-generated clinical summary with evidence-based analysis",
      "Full comprehensive test results organized by panel",
    ],
    sampleFile: "/sample-clinician-report.pdf",
    sampleLabel: "Sample Clinician Report",
  },
];

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function AiName({ name, goldStyle, restStyle }) {
  const parts = name.split(/(AI)/);
  return (
    <>
      {parts.map((p, i) =>
        p === "AI"
          ? <span key={i} style={{ color: "#c9a96e", fontWeight: 900, ...goldStyle }}>{p}</span>
          : <span key={i} style={restStyle}>{p}</span>
      )}
    </>
  );
}

function FeatureRow({ feat }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16,
      }}>
        {feat.icon}
      </div>
      <div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: "rgba(255,255,255,0.9)", marginBottom: 2 }}>
          {feat.label}
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.45)", lineHeight: 1.5 }}>
          {feat.detail}
        </div>
      </div>
    </div>
  );
}


function ToolCard({ tool, i }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`reveal delay-${i * 150}`}
      style={{
        background: hovered ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: hovered
          ? `1px solid rgba(201,169,110,0.35)`
          : "1px solid rgba(255,255,255,0.08)",
        borderRadius: 24,
        overflow: "hidden",
        position: "relative",
        transition: "all 0.4s cubic-bezier(.4,0,.2,1)",
        transform: hovered ? "translateY(-8px)" : "none",
        boxShadow: hovered ? "0 40px 100px rgba(0,0,0,0.45)" : "none",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top accent gradient */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${tool.accentFrom}, ${tool.accentTo}, rgba(201,169,110,0.5))`,
      }} />

      {/* Ambient glow */}
      <div style={{
        position: "absolute", top: -80, left: "50%", transform: "translateX(-50%)",
        width: 300, height: 300, borderRadius: "50%",
        background: `radial-gradient(circle, ${tool.accentFrom}18 0%, transparent 65%)`,
        pointerEvents: "none",
      }} />

      <div style={{ padding: "44px 44px 40px", position: "relative", zIndex: 1 }}>
        {/* Avatar + Eyebrow row */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          {tool.avatar && (
            <img
              src={tool.avatar}
              alt={tool.badge}
              style={{
                width: 64, height: 64, borderRadius: "50%",
                border: `2px solid ${tool.accentFrom}66`,
                objectFit: "cover", flexShrink: 0,
                boxShadow: `0 0 20px ${tool.accentFrom}44`,
              }}
            />
          )}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ width: 20, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 700,
                letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>
                {tool.eyebrow}
              </span>
            </div>
            {/* Badge */}
            <div style={{
              display: "inline-block",
              background: `linear-gradient(135deg, ${tool.accentFrom}22, ${tool.accentTo}22)`,
              border: `1px solid ${tool.accentFrom}44`,
              borderRadius: 8, padding: "4px 12px",
              fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 800,
              letterSpacing: "0.16em", color: BRAND.white,
            }}>
              <AiName name={tool.badge} restStyle={{ color: BRAND.white }} />
            </div>
          </div>
        </div>

        {/* Headline */}
        <h3 style={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 800,
          fontSize: "clamp(24px, 2.8vw, 34px)",
          color: BRAND.white,
          lineHeight: 1.15,
          margin: "0 0 10px",
          whiteSpace: "pre-line",
        }}>
          {tool.headline}
        </h3>

        {/* Tagline */}
        <p style={{
          fontFamily: "'Inter', sans-serif", fontSize: 15, fontStyle: "italic",
          color: BRAND.gold, marginBottom: 20, fontWeight: 400,
        }}>
          {tool.tagline}
        </p>

        {/* Description */}
        <p style={{
          fontFamily: "'Inter', sans-serif", fontSize: 16,
          color: "rgba(255,255,255,0.65)", lineHeight: 1.85,
          marginBottom: 32, fontWeight: 400,
        }}>
          {tool.desc}
        </p>

        {/* Features */}
        <div style={{ marginBottom: 36 }}>
          {tool.features.map((f, j) => <FeatureRow key={j} feat={f} />)}
        </div>

        {/* Labarium-only note */}
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontStyle: "italic",
          color: "rgba(255,255,255,0.5)", marginBottom: 24, lineHeight: 1.6 }}>
          Note: these tools only function with Labarium Diagnostic Systems lab reports.
        </p>

        {/* CTAs */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a
            href={tool.chatUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: `linear-gradient(135deg, ${tool.accentFrom}, ${tool.accentTo})`,
              color: BRAND.white,
              fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 12,
              letterSpacing: "0.08em", padding: "12px 22px",
              borderRadius: 9, textDecoration: "none",
              transition: "all 0.25s ease",
              boxShadow: `0 8px 24px ${tool.accentFrom}44`,
              textTransform: "uppercase",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 32px ${tool.accentFrom}66`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 8px 24px ${tool.accentFrom}44`; }}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 2h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9l-3 3v-3H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/>
            </svg>
            Launch <span style={{ marginLeft: 4 }}><AiName name={tool.badge} restStyle={{ color: BRAND.white }} /></span>
          </a>
          <button
            onClick={() => window.open(tool.sampleFile, "_blank")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "rgba(201,169,110,0.1)",
              border: "1px solid rgba(201,169,110,0.4)",
              color: BRAND.gold,
              fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 12,
              letterSpacing: "0.05em", padding: "11px 18px",
              borderRadius: 9, cursor: "pointer",
              transition: "all 0.22s ease",
              textTransform: "uppercase",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,169,110,0.18)"; e.currentTarget.style.borderColor = "rgba(201,169,110,0.7)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(201,169,110,0.1)"; e.currentTarget.style.borderColor = "rgba(201,169,110,0.4)"; }}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v8M4 6l3 3 3-3M2 11h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {tool.sampleLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main ────────────────────────────────────────────────────────────────── */
export default function LabariumIntelligencePage({ setPage }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);

  return (
    <>
      {/* ── HERO ── */}
      <section style={{
        background: `linear-gradient(150deg, ${BRAND.navyDeep} 0%, ${BRAND.navyDark} 50%, #001a45 100%)`,
        minHeight: "82vh",
        display: "flex", alignItems: "center",
        position: "relative", overflow: "hidden",
        paddingTop: 76,
      }}>
        {/* Ambient blobs */}
        <div style={{ position: "absolute", top: "8%", right: "6%", width: 520, height: 520, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(201,169,110,0.09) 0%, transparent 65%)",
          animation: "drift1 22s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "5%", left: "3%", width: 380, height: 380, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 65%)",
          animation: "drift2 28s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "55%", right: "28%", width: 240, height: 240, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 65%)",
          animation: "drift3 34s ease-in-out infinite", pointerEvents: "none" }} />

        {/* Grid */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.022, pointerEvents: "none" }}>
          <svg width="100%" height="100%">
            <defs><pattern id="intel-grid" width="52" height="52" patternUnits="userSpaceOnUse">
              <path d="M 52 0 L 0 0 0 52" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern></defs>
            <rect width="100%" height="100%" fill="url(#intel-grid)"/>
          </svg>
        </div>

        {/* Left accent */}
        <div style={{ position: "absolute", left: 0, top: 0, width: 3, height: "100%",
          background: `linear-gradient(180deg, transparent, ${BRAND.gold}, transparent)`,
          animation: "glowPulse 4s ease-in-out infinite" }} />

        <div className="rx-pad" style={{ maxWidth: 1400, margin: "0 auto", padding: "80px 40px", position: "relative", zIndex: 1, width: "100%" }}>
          <div style={{ maxWidth: 780 }}>

            {/* Eyebrow */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 28,
              opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(20px)",
              transition: "all 0.7s cubic-bezier(.4,0,.2,1)",
            }}>
              <div style={{ width: 36, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
                letterSpacing: "0.3em", color: BRAND.gold, textTransform: "uppercase" }}>
                Labarium Intelligence
              </span>
            </div>

            {/* Headline */}
            <h1 style={{
              fontFamily: "'Playfair Display', serif", fontWeight: 800,
              fontSize: "clamp(34px, 4.8vw, 68px)", lineHeight: 1.04,
              color: BRAND.white, margin: "0 0 24px", letterSpacing: "-0.015em",
              opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(32px)",
              transition: "all 0.85s cubic-bezier(.4,0,.2,1) 0.1s",
            }}>
              The Intelligence Behind<br />
              <span style={{
                background: `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldLight} 50%, ${BRAND.gold} 100%)`,
                backgroundSize: "200% auto", WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent", animation: "shimmer 4s linear infinite",
              }}>
                Every Decision
              </span>
            </h1>

            {/* Sub */}
            <p style={{
              fontFamily: "'Inter', sans-serif", fontSize: 18,
              color: "rgba(255,255,255,0.62)", lineHeight: 1.8,
              marginBottom: 16, maxWidth: 600, fontWeight: 400,
              opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(20px)",
              transition: "all 0.85s cubic-bezier(.4,0,.2,1) 0.22s",
            }}>
              Labarium Diagnostic Systems brings clinical AI directly to patients and clinicians — transforming raw lab data into clarity, confidence, and better health outcomes.
            </p>

            <p style={{
              fontFamily: "'Inter', sans-serif", fontSize: 15,
              color: "rgba(255,255,255,0.38)", lineHeight: 1.75,
              marginBottom: 44, maxWidth: 540, fontWeight: 400,
              opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(20px)",
              transition: "all 0.85s cubic-bezier(.4,0,.2,1) 0.32s",
            }}>
              Two AI tools available now. More intelligence coming soon.
            </p>

            {/* CTA row */}
            <div style={{
              display: "flex", gap: 14, flexWrap: "wrap",
              opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(20px)",
              transition: "all 0.85s cubic-bezier(.4,0,.2,1) 0.42s",
            }}>
              <a
                href="https://portal.labarium.com"
                target="_blank" rel="noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                  backgroundSize: "200% auto",
                  color: BRAND.navyDeep, border: "none",
                  fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 13,
                  letterSpacing: "0.08em", padding: "15px 32px",
                  borderRadius: 10, textDecoration: "none",
                  boxShadow: "0 8px 32px rgba(201,169,110,0.4)",
                  textTransform: "uppercase",
                  transition: "all 0.3s ease",
                }}
                className="btn-shimmer"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 2h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9l-3 3v-3H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/>
                </svg>
                Try Labarium AI
              </a>
              <button
                onClick={() => { const el = document.getElementById("tools-section"); el?.scrollIntoView({ behavior: "smooth" }); }}
                style={{
                  background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)",
                  color: "rgba(255,255,255,0.55)", border: "1.5px solid rgba(255,255,255,0.12)",
                  fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 13,
                  letterSpacing: "0.06em", padding: "15px 28px",
                  borderRadius: 10, cursor: "pointer", transition: "all 0.25s ease",
                  textTransform: "uppercase",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.color = BRAND.white; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
              >
                Explore ↓
              </button>
            </div>

            {/* Mission quote */}
            <div style={{
              marginTop: 52, paddingTop: 24,
              borderTop: "1px solid rgba(201,169,110,0.15)",
              borderLeft: `3px solid ${BRAND.gold}`, paddingLeft: 20,
              opacity: visible ? 1 : 0, transition: "all 0.8s ease 0.6s",
            }}>
              <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic",
                fontSize: 16, color: "rgba(255,255,255,0.55)", lineHeight: 1.9, margin: 0, maxWidth: 600 }}>
                Laboratory results inform 70% of all clinical decisions. With Labarium Intelligence, every result becomes a conversation — not just a number.{" "}
                <span style={{ color: BRAND.gold, fontWeight: 600 }}>That's the difference we're here to make.</span>
              </p>
            </div>
          </div>
        </div>

        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 100,
          background: "linear-gradient(transparent, rgba(0,14,36,0.5))", pointerEvents: "none" }} />
      </section>

      {/* ── STAT STRIP ── */}
      <div style={{ background: BRAND.navyDeep, borderTop: "1px solid rgba(201,169,110,0.12)",
        borderBottom: "1px solid rgba(201,169,110,0.12)", overflow: "hidden", padding: "14px 0" }}>
        <div style={{ display: "flex", animation: "tickerScroll 24s linear infinite", width: "max-content" }}>
          {[...Array(2)].flatMap(() => [
            "AIden — Patient AI Assistant",
            "ClAIre — Clinical Decision Support",
            "AI Report Archive",
            "HIPAA Compliant",
            "Zero PHI to AI Models",
            "16 Languages Supported",
            "Longitudinal Trend Memory",
            "Built by Labarium Diagnostic Systems",
          ]).map((item, i) => (
            <span key={i} style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500,
              color: "rgba(255,255,255,0.5)", letterSpacing: "0.08em", whiteSpace: "nowrap", padding: "0 44px" }}>
              <span style={{ color: BRAND.gold, marginRight: 16 }}>◆</span>{item}
            </span>
          ))}
        </div>
      </div>

      {/* ── AI CHATBOT TOOLS ── */}
      <section id="tools-section" className="rx-pad" style={{
        background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, #001535 100%)`,
        padding: "108px 40px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -100, right: -100, width: 600, height: 600, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(59,130,246,0.05) 0%, transparent 65%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -60, width: 500, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.05) 0%, transparent 65%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 1 }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 72 }}>
            <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
                letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>
                AI Chatbots
              </span>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
            </div>
            <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif",
              fontWeight: 700, fontSize: "clamp(28px,4vw,48px)", color: BRAND.white, margin: "0 0 18px" }}>
              Two <span style={{ color: BRAND.gold }}>AI</span>s. One Mission.
            </h2>
            <p className="reveal delay-200" style={{ fontFamily: "'Inter', sans-serif", fontSize: 16,
              color: "rgba(255,255,255,0.5)", maxWidth: 560, margin: "0 auto", lineHeight: 1.75, fontWeight: 400 }}>
              Whether you're a patient trying to understand your numbers or a clinician making critical decisions —{" "}
              there's a Labarium Intelligence tool built for you.
            </p>
          </div>

          {/* Tool cards — two column */}
          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
            {TOOLS.map((tool, i) => <ToolCard key={tool.id} tool={tool} i={i} />)}
          </div>

          {/* Disclaimer */}
          <div className="reveal delay-400" style={{
            marginTop: 40, padding: "18px 24px",
            background: "rgba(255,255,255,0.025)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 12,
          }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.55)",
              lineHeight: 1.75, margin: 0, textAlign: "center" }}>
              <strong style={{ color: "rgba(255,255,255,0.75)" }}>Important:</strong>{" "}
              AIden and ClAIre are AI-powered decision support tools designed to complement — not replace — professional medical care. Like all AI, they may occasionally misread values or make errors, and output quality is influenced by the clarity and completeness of the uploaded report. Always verify results and consult a licensed healthcare provider for diagnosis, treatment, or medical advice. These tools are optimized for use with Labarium Diagnostic Systems lab reports.
            </p>
          </div>
        </div>
      </section>

      {/* ── CHATBOT SCREENSHOT ── */}
      <section className="rx-pad" style={{
        background: BRAND.navyDeep,
        padding: "72px 40px 0",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <p className="reveal" style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 700,
            letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase", marginBottom: 16 }}>
            Inside the Platform
          </p>
          <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700,
            fontSize: "clamp(22px,3vw,36px)", color: BRAND.white, marginBottom: 32, lineHeight: 1.2 }}>
            Meet <AiName name="AIden" restStyle={{ color: BRAND.white }} /> &amp; <AiName name="ClAIre" restStyle={{ color: BRAND.white }} />
          </h2>
          <div className="reveal delay-200" style={{
            borderRadius: 20, overflow: "hidden",
            border: "1px solid rgba(201,169,110,0.2)",
            boxShadow: "0 40px 100px rgba(0,0,0,0.5)",
            display: "inline-block", maxWidth: "100%",
          }}>
            <img
              src="/labarium-chatbots.png"
              alt="AIden and ClAIre — Labarium AI Chatbot Platform"
              style={{ width: "100%", maxWidth: 960, display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* ── AI REPORTS ── */}
      <section className="rx-pad" style={{
        background: BRAND.offWhite,
        padding: "108px 40px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>

          {/* Section header */}
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
                letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>
                Labarium AI Report
              </span>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
            </div>
            {/* Coming Soon badge */}
            <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 7,
              background: "rgba(201,169,110,0.1)", border: "1px solid rgba(201,169,110,0.35)",
              borderRadius: 20, padding: "5px 14px", marginBottom: 20 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: BRAND.gold,
                animation: "glowPulse 2s ease-in-out infinite" }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 700,
                letterSpacing: "0.18em", color: BRAND.gold, textTransform: "uppercase" }}>
                Coming Soon
              </span>
            </div>
            <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif",
              fontWeight: 700, fontSize: "clamp(26px,3.5vw,46px)", color: BRAND.navy, margin: "0 0 18px", lineHeight: 1.1 }}>
              A Report Built for You —<br />Patient or Clinician
            </h2>
            <p className="reveal delay-200" style={{ fontFamily: "'Inter', sans-serif", fontSize: 16,
              color: BRAND.textMid, lineHeight: 1.8, maxWidth: 620, margin: "0 auto", fontWeight: 400 }}>
              The Labarium AI Report is a PDF you receive directly alongside your lab results. Patients get a plain-language version written for them. Clinicians get a clinical version written for them.
            </p>
          </div>

          {/* Register access bar */}
          <div className="reveal" style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 20,
            background: `linear-gradient(135deg, ${BRAND.navyDeep}, ${BRAND.navyDark})`,
            border: "1px solid rgba(201,169,110,0.2)",
            borderRadius: 16, padding: "28px 36px", marginBottom: 36,
            position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg, transparent, ${BRAND.gold}, transparent)` }} />
            <div>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 700,
                letterSpacing: "0.24em", textTransform: "uppercase", color: BRAND.gold, margin: "0 0 6px" }}>
                Get Started Today
              </p>
              <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700,
                fontSize: "clamp(17px,2vw,22px)", color: BRAND.white, margin: 0, lineHeight: 1.3 }}>
                Start using Labarium AI — and be first in line when AI Reports launch.
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16,
                color: "rgba(255,255,255,0.6)", margin: "6px 0 0", lineHeight: 1.65 }}>
                Register now to access AIden and ClAIre immediately. Labarium AI Reports are launching very soon — your account will be ready.
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13,
                color: "rgba(255,255,255,0.38)", margin: "8px 0 0", lineHeight: 1.6 }}>
                Patient registration requires a one-time $25 Labarium Lab Test Copay and Activation Fee. No subscriptions.
              </p>
            </div>
            <a
              href="https://portal.labarium.com/register"
              target="_blank" rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0,
                background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                backgroundSize: "200% auto",
                color: BRAND.navyDeep, textDecoration: "none",
                fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 13,
                letterSpacing: "0.08em", padding: "14px 30px",
                borderRadius: 10, textTransform: "uppercase",
                boxShadow: "0 6px 24px rgba(201,169,110,0.35)",
              }}
              className="btn-shimmer"
            >
              Register Now →
            </a>
          </div>

          {/* Two report type cards */}
          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
            {AI_REPORTS.map((card, i) => (
              <div
                key={i}
                className={`reveal delay-${i * 150}`}
                style={{
                  background: BRAND.white,
                  border: `1px solid #e2e8f0`,
                  borderRadius: 20, overflow: "hidden",
                  boxShadow: "0 8px 40px rgba(0,30,80,0.08)",
                  position: "relative",
                }}
              >
                <div style={{ height: 4, background: `linear-gradient(90deg, ${card.accentFrom}, ${card.accentTo})` }} />
                <div style={{ padding: "36px 40px" }}>
                  {/* Badge */}
                  <div style={{ display: "inline-block", background: `${card.accentFrom}18`,
                    border: `1px solid ${card.accentFrom}44`, borderRadius: 6,
                    padding: "4px 14px", fontFamily: "'Inter', sans-serif",
                    fontSize: 12, fontWeight: 800, letterSpacing: "0.18em", color: card.accentFrom,
                    textTransform: "uppercase", marginBottom: 16 }}>
                    {card.badge}
                  </div>

                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(20px,2.2vw,26px)",
                    fontWeight: 700, color: BRAND.navy, lineHeight: 1.2, margin: "0 0 16px" }}>
                    {card.headline}
                  </h3>

                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: BRAND.textMid,
                    lineHeight: 1.85, marginBottom: 28 }}>{card.desc}</p>

                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 32px" }}>
                    {card.features.map((f, j) => (
                      <li key={j} style={{ display: "flex", gap: 10, alignItems: "flex-start",
                        fontFamily: "'Inter', sans-serif", fontSize: 15, color: BRAND.textMid,
                        lineHeight: 1.65, paddingBottom: 10,
                        borderBottom: j < card.features.length - 1 ? `1px solid #f0f4f8` : "none",
                        marginBottom: j < card.features.length - 1 ? 10 : 0 }}>
                        <span style={{ color: card.accentFrom, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* Single CTA: download */}
                  <button
                    onClick={() => { if (card.sampleFile) window.open(card.sampleFile, "_blank"); }}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      background: card.sampleFile ? `linear-gradient(135deg, ${card.accentFrom}, ${card.accentTo})` : BRAND.white,
                      border: card.sampleFile ? "none" : `1.5px solid #cbd5e1`,
                      color: card.sampleFile ? BRAND.white : BRAND.textLight,
                      fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 12,
                      letterSpacing: "0.06em", padding: "12px 22px", borderRadius: 9,
                      cursor: card.sampleFile ? "pointer" : "not-allowed",
                      textTransform: "uppercase",
                      boxShadow: card.sampleFile ? `0 6px 20px ${card.accentFrom}44` : "none",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1v8M4 6l3 3 3-3M2 11h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {card.sampleFile ? card.sampleLabel : `${card.sampleLabel} (coming soon)`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="rx-pad" style={{
        background: BRAND.white, padding: "88px 40px",
        borderTop: `1px solid ${BRAND.lightGray}`,
      }}>
        <div style={{ maxWidth: 680, margin: "0 auto", textAlign: "center" }}>
          <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ width: 48, height: 1, background: BRAND.gold }} />
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
              letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>
              Get Started
            </span>
            <div style={{ width: 48, height: 1, background: BRAND.gold }} />
          </div>
          <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif",
            fontWeight: 700, fontSize: "clamp(26px,3.5vw,44px)", color: BRAND.navy,
            margin: "0 0 18px", lineHeight: 1.15 }}>
            Ready to Get Started?
          </h2>
          <p className="reveal delay-200" style={{ fontFamily: "'Inter', sans-serif", fontSize: 16,
            color: BRAND.textLight, marginBottom: 16, lineHeight: 1.75, fontWeight: 400 }}>
            Register to access <AiName name="AIden" restStyle={{ color: BRAND.textLight }} /> and <AiName name="ClAIre" restStyle={{ color: BRAND.textLight }} /> today — and be ready the moment Labarium AI Reports arrive, coming very soon.
          </p>
          <p className="reveal delay-250" style={{ fontFamily: "'Inter', sans-serif", fontSize: 14,
            color: BRAND.textLight, opacity: 0.6, marginBottom: 40, lineHeight: 1.65 }}>
            Patient registration includes a one-time $25 Labarium Lab Test Copay and Activation Fee. No subscriptions.
          </p>
          <div className="reveal delay-300" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a
              href="https://portal.labarium.com/register"
              target="_blank" rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                backgroundSize: "200% auto",
                color: BRAND.navyDeep, border: "none",
                fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 13,
                letterSpacing: "0.08em", padding: "16px 36px",
                borderRadius: 10, textDecoration: "none",
                boxShadow: "0 8px 32px rgba(201,169,110,0.4)",
                textTransform: "uppercase",
              }}
              className="btn-shimmer"
            >
              Register for Access
            </a>
            <a
              href="https://portal.labarium.com"
              target="_blank" rel="noreferrer"
              style={{
                background: "transparent", color: BRAND.textLight,
                border: `1.5px solid #e2e8f0`,
                fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 13,
                letterSpacing: "0.06em", padding: "16px 36px",
                borderRadius: 10, textDecoration: "none", display: "inline-flex",
                alignItems: "center", transition: "all 0.25s ease",
                textTransform: "uppercase",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.color = BRAND.navy; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = BRAND.textLight; }}
            >
              Try Labarium AI
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
